import React, { useState } from "react";
import { Label } from "@/components/ui/label";
import {
  Popover, PopoverContent, PopoverTrigger,
} from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { X, Plus, Users } from "lucide-react";

// Multi-select picker for assistants. value/onChange operate on a string[] of team_member.id.
// Filters team to roles 'assistente' / legacy 'assistant' / 'both'.
export default function AssistantsPicker({ value = [], onChange, team = [], testid }) {
  const [open, setOpen] = useState(false);

  const eligible = team.filter((m) => {
    const r = (m.role || "").toLowerCase();
    return r === "assistente" || r === "assistant" || r === "both";
  });

  const selected = value
    .map((id) => team.find((m) => m.id === id))
    .filter(Boolean);

  const available = eligible.filter((m) => !value.includes(m.id));

  const add = (id) => {
    if (!value.includes(id)) onChange([...value, id]);
    setOpen(false);
  };
  const remove = (id) => onChange(value.filter((x) => x !== id));

  return (
    <div className="bg-white border border-border rounded-sm p-5" data-testid={testid}>
      <div className="flex items-center gap-3 flex-wrap">
        <span className="w-9 h-9 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
          <Users className="w-4 h-4 text-primary" strokeWidth={1.5} />
        </span>
        <div className="min-w-0 flex-1">
          <Label className="label-uppercase">Operatori assistenti</Label>
          <p className="text-xs text-neutral-500 mt-0.5">
            Aggiungi più membri del team che assisteranno l'evento.
          </p>
        </div>
      </div>

      {/* Selected chips */}
      <div className="mt-4 flex flex-wrap gap-2 items-center">
        {selected.length === 0 && (
          <span className="text-xs text-neutral-400 italic">Nessun assistente</span>
        )}
        {selected.map((m) => (
          <span
            key={m.id}
            className="inline-flex items-center gap-1.5 bg-secondary border border-border rounded-full pl-2 pr-1 py-1 text-xs"
            data-testid={`${testid}-chip-${m.id}`}
          >
            <span
              className="w-2 h-2 rounded-full"
              style={{ backgroundColor: m.color || "#94A3B8" }}
            />
            <span className="font-medium">{m.name}</span>
            <button
              type="button"
              onClick={() => remove(m.id)}
              className="w-5 h-5 inline-flex items-center justify-center rounded-full text-neutral-400 hover:text-destructive hover:bg-destructive/5 transition-colors"
              data-testid={`${testid}-remove-${m.id}`}
              aria-label={`Rimuovi ${m.name}`}
            >
              <X className="w-3 h-3" strokeWidth={1.5} />
            </button>
          </span>
        ))}

        {/* Add picker */}
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="rounded-full h-7 px-3 text-xs"
              disabled={available.length === 0}
              data-testid={`${testid}-add-trigger`}
            >
              <Plus className="w-3.5 h-3.5 mr-1" strokeWidth={1.5} />
              {available.length === 0 ? "Tutti aggiunti" : "Aggiungi"}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-64 p-2 rounded-sm" align="start">
            {available.length === 0 ? (
              <p className="text-xs text-neutral-500 p-2">
                Nessun assistente disponibile. Aggiungi membri con ruolo "Assistente" dalla pagina Team.
              </p>
            ) : (
              <ul className="max-h-64 overflow-y-auto" data-testid={`${testid}-options`}>
                {available.map((m) => (
                  <li key={m.id}>
                    <button
                      type="button"
                      onClick={() => add(m.id)}
                      className="w-full flex items-center gap-2 px-2 py-2 rounded-sm hover:bg-secondary/60 transition-colors text-left text-sm"
                      data-testid={`${testid}-option-${m.id}`}
                    >
                      <span
                        className="w-2.5 h-2.5 rounded-full shrink-0"
                        style={{ backgroundColor: m.color || "#94A3B8" }}
                      />
                      <span className="truncate flex-1">{m.name}</span>
                      <span className="text-[10px] text-neutral-400 uppercase tracking-wider">
                        {m.role === "both" ? "Entrambi" : "Assistente"}
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </PopoverContent>
        </Popover>
      </div>
    </div>
  );
}
